import { z } from 'zod'
import { decimalSchema, timestampSchema, uuidSchema } from './common.ts'

export const EQUIPMENT_REPORT_PERIODS = ['day', 'week', 'month'] as const
export const equipmentReportPeriodSchema = z.enum(EQUIPMENT_REPORT_PERIODS)
export type EquipmentReportPeriod = z.infer<typeof equipmentReportPeriodSchema>

export const equipmentReportQuerySchema = z.object({
  period: equipmentReportPeriodSchema.default('day'),
  /** Any day inside the period, Kyiv calendar. Defaults to today. */
  date: z.iso.date().optional(),
})
export type EquipmentReportQuery = z.infer<typeof equipmentReportQuerySchema>

/** Output is totalled per unit: kilograms and pieces are never added together. */
export const equipmentOutputSchema = z.object({
  /** The grouping key: two units may share a display symbol. */
  unitCode: z.string().nullable(),
  unitSymbol: z.string().nullable(),
  good: decimalSchema,
  scrap: decimalSchema,
})
export type EquipmentOutput = z.infer<typeof equipmentOutputSchema>

export const equipmentSessionSchema = z.object({
  id: uuidSchema,
  operatorId: z.string(),
  operatorName: z.string(),
  orderId: uuidSchema,
  orderNumber: z.string(),
  stageLabel: z.string(),
  itemName: z.string(),
  startedAt: timestampSchema,
  /** Null while the session is still running; `minutes` then counts up to now. */
  endedAt: timestampSchema.nullable(),
  minutes: z.int().nonnegative(),
  unitCode: z.string().nullable(),
  unitSymbol: z.string().nullable(),
  good: decimalSchema,
  scrap: decimalSchema,
})
export type EquipmentSession = z.infer<typeof equipmentSessionSchema>

export const equipmentRepairPeriodSchema = z.object({
  id: uuidSchema,
  reason: z.string(),
  startedAt: timestampSchema,
  endedAt: timestampSchema.nullable(),
  startedByName: z.string().nullable(),
})
export type EquipmentRepairPeriod = z.infer<typeof equipmentRepairPeriodSchema>

export const equipmentReportDaySchema = z.object({
  date: z.iso.date(),
  workday: z.boolean(),
  /** Days after today have no data yet. */
  future: z.boolean(),
  workedMinutes: z.int().nonnegative(),
  repairMinutes: z.int().nonnegative(),
  output: z.array(equipmentOutputSchema),
})
export type EquipmentReportDay = z.infer<typeof equipmentReportDaySchema>

export const equipmentReportSchema = z.object({
  equipmentId: uuidSchema,
  period: equipmentReportPeriodSchema,
  from: z.iso.date(),
  to: z.iso.date(),
  /** The machine's minutes per day, or the 480-minute shift when none is set. */
  shiftMinutes: z.int().positive(),
  days: z.array(equipmentReportDaySchema),
  sessions: z.array(equipmentSessionSchema),
  repairs: z.array(equipmentRepairPeriodSchema),
  workedMinutes: z.int().nonnegative(),
  availableMinutes: z.int().nonnegative(),
  repairMinutes: z.int().nonnegative(),
  utilizationPercent: z.int().nonnegative(),
  output: z.array(equipmentOutputSchema),
  byOperator: z.array(
    z.object({
      operatorId: z.string(),
      operatorName: z.string(),
      minutes: z.int().nonnegative(),
      output: z.array(equipmentOutputSchema),
    }),
  ),
})
export type EquipmentReport = z.infer<typeof equipmentReportSchema>
